/* eslint-disable react/no-multi-comp */
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectImportList, requestImportList, selectCurrentImport } from '../../../store/imports';
import { TableDefinition, Column, CellTypes } from '../../../Components/DataTable/DataTable.types';
import { useTranslation } from 'react-i18next';
import { useDateFns } from '../../../Hooks';
import { ImportState } from '../../../Models/DTO/Import.model';
import { SimpleProgressBar } from '../../../Components/SimpleProgressBar';
import { makeStyles } from '~/UIKit/Style';
import { PRIMARY_BLUE, NOTE_RED } from '../../../Constants/Colors.constants';
import { useThunkDispatch } from '../../../Helpers/Action.helpers';
import { bindActionCreators } from 'redux';
import { selectCurrentProvider } from '../../../store/provider';

const useProgressBarStyles = makeStyles(() => ({
  progressBar: {
    flex: '0 0 140px',
  },
  progressBarWrapper: {
    display: 'flex',
  },
  newImportText: {
    color: PRIMARY_BLUE,
  },
  errorText: {
    color: NOTE_RED,
  },
}));

export const useImportList = () => {
  const { t } = useTranslation();
  const { formatDate } = useDateFns();
  const { classes } = useProgressBarStyles();
  const dispatch = useThunkDispatch();

  const importList = useSelector(selectImportList);
  const currentProvider = useSelector(selectCurrentProvider);
  const currentImport = useSelector(selectCurrentImport);

  const getImportList = bindActionCreators(requestImportList, dispatch);

  const getImportStatusTranslation = (state: ImportState) => {
    switch (state) {
      case 'Completed':
        return t('app.import.status.complete');
      case 'FileParsingFailed':
      case 'ProcessingFailed':
        return t('app.import.status.error');
      default:
        return t('app.import.status.inprogress');
    }
  };

  const getImportColumns = (): Column[] => [
    {
      label: t('app.import.table.date'),
      propertyIds: 'createdDate',
      cellType: CellTypes.String,
      formatter: (value) => formatDate(value, 'Pp'),
    },
    {
      label: t('app.import.table.progress'),
      propertyIds: 'status',
      cellType: CellTypes.String,
      formatter: (value: ImportState) => {
        let progress = 0;
        switch (value) {
          case 'Processing':
            progress = 0.5;
            break;
          case 'ProcessingFailed':
          case 'Completed':
            progress = 1;
            break;
          default:
            progress = 0;
            break;
        }

        return (
          <div className={classes.progressBarWrapper}>
            <SimpleProgressBar max={1} value={progress} className={classes.progressBar} />
          </div>
        );
      },
    },
    {
      label: t('app.import.table.status'),
      propertyIds: 'status',
      cellType: CellTypes.String,
      formatter: (value: ImportState) => {
        const translation = getImportStatusTranslation(value);
        let className = value === 'New' ? classes.newImportText : '';
        if (value === 'FileParsingFailed' || value === 'ProcessingFailed')
          className += classes.errorText;

        return <div className={className}>{translation}</div>;
      },
    },
  ];

  const getTableDefinition = () => {
    const tableDefinition: TableDefinition = {
      columns: getImportColumns(),
      config: {
        useSelectableRows: true,
      },
    };

    return tableDefinition;
  };

  useEffect(() => {
    if (currentProvider?.uuid) {
      getImportList(currentProvider.uuid);
    }
  }, [currentProvider, currentImport?.status]);

  return {
    importList,
    tableDefinition: getTableDefinition(),
  };
};
